import React from 'react';
import millify from 'millify';

export interface WithChange {
  value: number;
  change: number;
}

export interface ActivityData {
  filter: string;
  newCampaigns: WithChange;
  completedCampaigns: WithChange;
  newBrands: WithChange;
  newCreators: WithChange;
  gmv: WithChange;
  cashbackPaid: WithChange;
  submissions: WithChange;
  approvalRate: WithChange;
  newSubscriptions: WithChange;
}

interface ActivityPeriodRowProps {
  data: ActivityData | null;
  loading: boolean;
}

const Chip: React.FC<{
  value: number;
  change: number;
  prefix?: string;
  suffix?: string;
}> = ({ value, change, prefix = '', suffix = '' }) => {
  const isPositive = change >= 0;

  return (
    <div className="flex flex-col gap-1">
      <span className="text-2xl font-bold text-[#172B4D] font-header">
        {prefix}
        {typeof value === 'number' && value > 9999
          ? millify(value)
          : value.toLocaleString()}
        {suffix}
      </span>
      <span
        className={`inline-flex items-center gap-0.5 text-xs font-semibold px-2 py-0.5 rounded-full w-fit ${
          isPositive
            ? 'bg-[#E6F8F0] text-[#00AE3E]'
            : 'bg-[#FFE5E5] text-[#DC2626]'
        }`}
      >
        {isPositive ? '↑' : '↓'} {Math.abs(change)}%
      </span>
    </div>
  );
};

const SkeletonChip = () => (
  <div className="animate-pulse flex flex-col gap-2">
    <div className="h-8 w-20 bg-gray-200 rounded" />
    <div className="h-5 w-12 bg-gray-200 rounded-full" />
  </div>
);

const ActivityPeriodRow: React.FC<ActivityPeriodRowProps> = ({
  data,
  loading,
}) => {
  const items = [
    {
      label: 'New Campaigns',
      value: data?.newCampaigns.value ?? 0,
      change: data?.newCampaigns.change ?? 0,
    },
    {
      label: 'Completed',
      value: data?.completedCampaigns.value ?? 0,
      change: data?.completedCampaigns.change ?? 0,
    },
    {
      label: 'New Brands',
      value: data?.newBrands.value ?? 0,
      change: data?.newBrands.change ?? 0,
    },
    {
      label: 'New Creators',
      value: data?.newCreators.value ?? 0,
      change: data?.newCreators.change ?? 0,
    },
    {
      label: 'GMV',
      value: data?.gmv.value ?? 0,
      change: data?.gmv.change ?? 0,
      prefix: '€',
    },
    {
      label: 'Cashback Paid',
      value: data?.cashbackPaid.value ?? 0,
      change: data?.cashbackPaid.change ?? 0,
      prefix: '€',
    },
    {
      label: 'Submissions',
      value: data?.submissions.value ?? 0,
      change: data?.submissions.change ?? 0,
    },
    {
      label: 'Approval Rate',
      value: data?.approvalRate.value ?? 0,
      change: data?.approvalRate.change ?? 0,
      suffix: '%',
    },
    {
      label: 'New Subs',
      value: data?.newSubscriptions.value ?? 0,
      change: data?.newSubscriptions.change ?? 0,
    },
  ];

  return (
    <div className="bg-white border border-[#E5E9EB] rounded-3xl p-6">
      {/* divide-x only at lg where all 9 items fit in one row; below that, use gap */}
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-9 gap-y-6 gap-x-0 lg:divide-x lg:divide-[#F0F0F0]">
        {items.map((item, i) => (
          <div
            key={item.label}
            className={`flex flex-col gap-2 ${i > 0 ? 'lg:pl-4' : ''} px-2 sm:px-0 sm:pr-4 lg:px-4 lg:first:pl-0`}
          >
            <span className="text-[11px] text-[#9B919D] font-medium uppercase tracking-wide min-h-[2rem] flex items-start">
              {item.label}
            </span>
            {loading ? (
              <SkeletonChip />
            ) : (
              <Chip
                value={item.value}
                change={item.change}
                prefix={item.prefix}
                suffix={item.suffix}
              />
            )}
          </div>
        ))}
      </div>
    </div>
  );
};

export default ActivityPeriodRow;
